import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Toast,
} from '@/components/ui';
import { WINDOWS, formatMoney, formatNumber } from '@/lib/format';

interface CostRow {
  kind: string;
  model: string | null;
  provider_name: string | null;
  quantity: string;
  actual_cost: string;
  client_price: string;
  events: number;
}

const KIND_LABELS: Record<string, string> = {
  LLM_INPUT_TOKENS: 'AI input tokens',
  LLM_OUTPUT_TOKENS: 'AI output tokens',
  STT_SECONDS: 'Speech recognition',
  TTS_CHARACTERS: 'Speech synthesis',
  TELEPHONY_MINUTES: 'Telephony minutes',
};

export default function Costs() {
  const [days, setDays] = useState(30);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin-costs', days],
    queryFn: () => api.get<{ data: CostRow[] }>('/admin/costs', { days }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const totalCost = data.data.reduce((sum, row) => sum + Number(row.actual_cost), 0);
  const totalRevenue = data.data.reduce((sum, row) => sum + Number(row.client_price), 0);

  return (
    <div className="w-full">
      <PageHeader
        title="Provider costs"
        description="Where the money actually goes, by provider and model."
        actions={
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-auto"
          >
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                Last {w.label}
              </option>
            ))}
          </Select>
        }
      />

      <Card padded={false}>
        <div className="overflow-x-auto">
          <table className="glee-table">
            <thead>
              <tr>
                {['Provider', 'Category', 'Model', 'Events', 'Quantity', 'Cost', 'Billed', 'Margin'].map(
                  (header, index) => (
                    <th
                      key={header}
                      className={`${
                        index >= 3 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {data.data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-sm text-ink-500">
                    No usage in this window.
                  </td>
                </tr>
              ) : (
                data.data.map((row, index) => {
                  const cost = Number(row.actual_cost);
                  const billed = Number(row.client_price);
                  return (
                    <tr key={index}>
                      <td>
                        {row.provider_name ?? (
                          <span className="text-amber-600">Unattributed</span>
                        )}
                      </td>
                      <td className="text-xs">
                        {KIND_LABELS[row.kind] ?? row.kind.replace(/_/g, ' ')}
                      </td>
                      <td className="font-mono text-xs text-ink-500">
                        {row.model ?? '—'}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatNumber(row.events)}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatNumber(Math.round(Number(row.quantity)))}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatMoney(cost)}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatMoney(billed)}
                      </td>
                      <td
                        className={`text-right tabular-nums ${
                          billed - cost < 0 ? 'text-red-600' : 'text-emerald-600'
                        }`}
                      >
                        {formatMoney(billed - cost)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {data.data.length > 0 && (
              <tfoot>
                <tr className="font-medium">
                  <td colSpan={5}>
                    Total
                  </td>
                  <td className="text-right tabular-nums">
                    {formatMoney(totalCost)}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatMoney(totalRevenue)}
                  </td>
                  <td className="text-right tabular-nums text-emerald-600">
                    {formatMoney(totalRevenue - totalCost)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Said only when there is something to say it about. A permanent
          footnote explaining a word that does not appear on the page reads
          like a known fault nobody has got round to. */}
      {data.data.some((row) => !row.provider_name) && (
        <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
          &ldquo;Unattributed&rdquo; rows are usage recorded without a provider link — usually a
          mock-mode run, or a provider that was deleted after the usage was billed.
          Most can be matched back to a provider; use <em>Fix or reset costs</em> below.
        </p>
      )}

      <CostMaintenance onDone={() => refetch()} />
    </div>
  );
}

interface PurgePreview {
  deleted: boolean;
  would_delete: {
    events: number;
    billed_events: number;
    unattributed: number;
    actual_cost: number;
    client_price: number;
    clients: number;
  };
  note: string;
}

interface RepairPreview {
  repaired: boolean;
  unattributed: number;
  can_repair: number;
  cannot_repair: number;
  rows_repaired?: number;
  still_unattributed?: number;
  note?: string;
}

const RESET_PHRASE = 'RESET ALL COSTS';

/**
 * Fixing the ledger, and clearing it.
 *
 * Deliberately in this order. The repair keeps every figure and only answers
 * the question this page could not — which provider a cost was paid to — so
 * it is what somebody with real usage wants. The reset is underneath it,
 * behind a typed phrase, for a platform coming out of testing where every
 * number describes calls nobody was charged for.
 */
function CostMaintenance({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [includeBilled, setIncludeBilled] = useState(false);
  const [preview, setPreview] = useState<PurgePreview['would_delete'] | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-costs'] });
    onDone();
  };

  const repairCheck = useQuery({
    queryKey: ['usage-repair-check'],
    queryFn: () => api.post<RepairPreview>('/admin/usage/repair-attribution', {}),
    enabled: open,
  });

  const repair = useMutation({
    mutationFn: () =>
      api.post<RepairPreview>('/admin/usage/repair-attribution?confirm=true', {}),
    onSuccess: (result) => {
      setToast({
        message:
          result.rows_repaired === 0
            ? 'Nothing needed attributing.'
            : `${result.rows_repaired} row${result.rows_repaired === 1 ? '' : 's'} attributed.` +
              (result.still_unattributed
                ? ` ${result.still_unattributed} could not be matched.`
                : ''),
        tone: 'good',
      });
      queryClient.invalidateQueries({ queryKey: ['usage-repair-check'] });
      refresh();
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not repair'), tone: 'bad' }),
  });

  const check = useMutation({
    mutationFn: () =>
      api.post<PurgePreview>('/admin/usage/purge', { include_billed: includeBilled }),
    onSuccess: (result) => setPreview(result.would_delete),
    onError: (err) => setToast({ message: errorMessage(err, 'Could not check'), tone: 'bad' }),
  });

  const purge = useMutation({
    mutationFn: () =>
      api.post<{ events_deleted: number; rollups_deleted: number }>('/admin/usage/purge', {
        confirm: true,
        confirm_phrase: phrase,
        include_billed: includeBilled,
      }),
    onSuccess: (result) => {
      setToast({
        message: `Cleared ${result.events_deleted} usage events. Invoices were not touched.`,
        tone: 'good',
      });
      setPhrase('');
      setPreview(null);
      setOpen(false);
      refresh();
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Could not clear'), tone: 'bad' }),
  });

  if (!open) {
    return (
      <div className="mt-4">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Fix or reset costs
        </Button>
        {toast && (
          <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
        )}
      </div>
    );
  }

  const unattributed = repairCheck.data?.unattributed ?? 0;
  const canRepair = repairCheck.data?.can_repair ?? 0;

  return (
    <div className="mt-4 space-y-4">
      <Card title="Fix provider attribution">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          A row shows as <span className="text-amber-600">Unattributed</span> when
          the usage was recorded without a provider link. The cost is real and it
          is already inside the totals above — the only thing missing is who it
          was paid to. This works that out from the model and the rate card, and
          changes nothing else.
        </p>

        {repairCheck.isPending ? (
          <Loading label="Checking…" />
        ) : unattributed === 0 ? (
          <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-400">
            Every row has a provider. Nothing to fix.
          </p>
        ) : (
          <>
            <p className="mt-3 text-sm">
              <strong>{formatNumber(unattributed)}</strong> unattributed row
              {unattributed === 1 ? '' : 's'}, of which{' '}
              <strong>{formatNumber(canRepair)}</strong> can be matched to a
              provider.
              {repairCheck.data && repairCheck.data.cannot_repair > 0 && (
                <>
                  {' '}
                  The other {formatNumber(repairCheck.data.cannot_repair)} cannot
                  — usually mock-mode runs, where there was no provider because
                  no provider was called. Those are left alone rather than
                  guessed at.
                </>
              )}
            </p>
            <div className="mt-3">
              <Button
                loading={repair.isPending}
                disabled={canRepair === 0}
                onClick={() => repair.mutate()}
              >
                Attribute {formatNumber(canRepair)} row{canRepair === 1 ? '' : 's'}
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card title="Reset the cost ledger">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          Deletes metered usage and the per-call cost rollups, so the figures
          start again from the next call. For a platform coming out of testing.
        </p>
        <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
          <strong>Invoices are never touched.</strong> An invoice is a statement
          that was sent to somebody; this is the working behind it. Calls,
          transcripts and transfers are not touched either — use{' '}
          <em>Clear old call history</em> for those.
        </p>

        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeBilled}
            onChange={(e) => {
              setIncludeBilled(e.target.checked);
              setPreview(null);
            }}
            className="mt-0.5"
          />
          <span>
            Also delete usage already attached to an invoice
            <span className="block text-xs text-ink-500">
              That usage is the evidence behind a charge. Removing it leaves the
              invoice standing with nothing to justify it — fine if every invoice
              so far is a test, not otherwise.
            </span>
          </span>
        </label>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            loading={check.isPending}
            onClick={() => check.mutate()}
          >
            Check what would go
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Close
          </Button>
        </div>

        {preview && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
            <p>
              This would delete <strong>{formatNumber(preview.events)}</strong> usage
              event{preview.events === 1 ? '' : 's'} across{' '}
              {formatNumber(preview.clients)} client
              {preview.clients === 1 ? '' : 's'}, worth{' '}
              {formatMoney(preview.actual_cost)} in provider cost and{' '}
              {formatMoney(preview.client_price)} billed.
              {preview.billed_events > 0 && (
                <>
                  {' '}
                  {formatNumber(preview.billed_events)} of them are already on an
                  invoice.
                </>
              )}
            </p>

            {preview.events === 0 ? (
              <p className="mt-2">There is nothing to delete.</p>
            ) : (
              <>
                <div className="mt-3 max-w-xs">
                  <Field label={`Type ${RESET_PHRASE} to confirm`}>
                    <Input
                      value={phrase}
                      onChange={(e) => setPhrase(e.target.value)}
                      placeholder={RESET_PHRASE}
                    />
                  </Field>
                </div>
                <Button
                  className="mt-2"
                  loading={purge.isPending}
                  disabled={phrase.trim() !== RESET_PHRASE}
                  onClick={() => purge.mutate()}
                >
                  Delete and start again
                </Button>
              </>
            )}
          </div>
        )}
      </Card>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

